import {
  ROUTE_CONFIG,
  SUPPORTED_LANGS,
  buildLocalizedPath,
  type Lang,
  type RouteKey,
} from "~/config/route-config";

function pathSegments(path: string): string[] {
  return path.split("/").filter(Boolean);
}

/** Match a single pattern (e.g. `/en/dashboard/module/:id`) against a full pathname. */
function matchPathPattern(pattern: string, pathname: string): Record<string, string> | null {
  const normalized = pathname.replace(/\/$/, "") || "/";
  const pat = pattern.replace(/\/$/, "") || "/";
  const pSegs = pathSegments(pat);
  const uSegs = pathSegments(normalized);
  if (pSegs.length !== uSegs.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < pSegs.length; i++) {
    const p = pSegs[i];
    const u = uSegs[i];
    if (p.startsWith(":")) {
      params[p.slice(1)] = decodeURIComponent(u);
    } else if (p !== u) {
      return null;
    }
  }
  return params;
}

/**
 * Finds which `RouteKey` and params correspond to the current URL (any supported language).
 */
export function resolveRouteFromPathname(pathname: string): {
  routeKey: RouteKey;
  params: Record<string, string>;
  lang: Lang;
} | null {
  const clean = pathname.split("?")[0].split("#")[0].replace(/\/$/, "") || "/";

  const candidates: { key: RouteKey; lang: Lang; pattern: string }[] = [];
  for (const key of Object.keys(ROUTE_CONFIG) as RouteKey[]) {
    for (const lang of SUPPORTED_LANGS) {
      candidates.push({ key, lang, pattern: ROUTE_CONFIG[key].paths[lang] });
    }
  }
  candidates.sort((a, b) => b.pattern.length - a.pattern.length);

  for (const c of candidates) {
    const pat = c.pattern.replace(/\/$/, "") || "/";
    const params = matchPathPattern(pat, clean);
    if (params !== null) {
      return { routeKey: c.key, params, lang: c.lang };
    }
  }
  return null;
}

/**
 * Same logical page in another language: `/en/dashboard` ↔ `/es/panel`, `/en/support` ↔ `/es/soporte`, etc.
 */
export function swapLocaleInPath(pathname: string, newLang: Lang): string {
  const clean = pathname.split("?")[0].split("#")[0];
  const resolved = resolveRouteFromPathname(clean);
  if (resolved) {
    const { routeKey, params } = resolved;
    const p = Object.keys(params).length > 0 ? params : undefined;
    return buildLocalizedPath(routeKey, newLang, p);
  }

  // Legal slug routes are not in ROUTE_CONFIG patterns; only the locale prefix changes.
  if (/^\/(en|es)\/legal\//.test(clean)) {
    return clean.replace(/^\/(en|es)(?=\/)/, `/${newLang}`);
  }

  const segments = clean.split("/").filter(Boolean);
  if (segments.length > 0 && SUPPORTED_LANGS.includes(segments[0] as Lang)) {
    segments[0] = newLang;
    return `/${segments.join("/")}`;
  }
  return `/${newLang}`;
}
